import { useEffect, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";

interface HeroSlide {
  id: string;
  title: string;
  summary: string;
  tag: string;
  cover: string;
}

const AUTOPLAY_MS = 5500;

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  const goTo = (index: number) => {
    setActive((index + slides.length) % slides.length);
  };

  const openSlide = (id: string) => {
    navigate(`/detail/${id}`);
  };

  const onTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta > 0 ? active - 1 : active + 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative h-[420px] w-full overflow-hidden rounded-lg md:h-[560px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点推荐轮播"
    >
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <button
            type="button"
            onClick={() => openSlide(slide.id)}
            className="absolute inset-0 h-full w-full cursor-pointer"
            aria-label={`查看：${slide.title}`}
          >
            <img
              src={slide.cover}
              alt={slide.title}
              title={`${slide.title}｜星空动漫`}
              className={`h-full w-full object-cover object-top ${
                index === active ? "animate-slow-zoom" : ""
              }`}
            />
            <span className="absolute inset-0 bg-gradient-to-tr from-primary-950/85 via-primary-950/50 to-primary-950/20" />
          </button>

          <div className="pointer-events-none absolute inset-0 flex items-end">
            <div className="w-full px-5 pb-10 md:px-12 md:pb-14">
              <div className="max-w-2xl">
                <span className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-accent-950">
                  <i className="ri-star-smile-line" />
                  {slide.tag}
                </span>
                <h2 className="mt-4 text-2xl font-bold leading-snug text-background-50 md:text-4xl">
                  {slide.title}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
                  {slide.summary}
                </p>
                <button
                  type="button"
                  onClick={() => openSlide(slide.id)}
                  className="pointer-events-auto mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-accent-100"
                >
                  阅读全文
                  <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => goTo(active - 1)}
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/80 text-primary-800 transition-colors hover:bg-background-50 md:left-6 md:h-10 md:w-10"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(active + 1)}
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/80 text-primary-800 transition-colors hover:bg-background-50 md:right-6 md:h-10 md:w-10"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 right-5 flex items-center gap-2 md:right-10">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 cursor-pointer rounded-full transition-all ${
              index === active ? "w-7 bg-accent-400" : "w-3 bg-background-50/60 hover:bg-background-50"
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}